// Расширенная система валидации медицинских данных
export interface ValidationRule {
  id: string;
  name: string;
  description: string;
  category: 'safety' | 'accuracy' | 'completeness' | 'consistency';
  severity: 'low' | 'medium' | 'high' | 'critical';
  validator: (data: any) => ValidationResult;
  errorMessage: string;
  warningMessage?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  score: number; // 0-100
  timestamp: Date;
}

export interface ValidationError {
  id: string;
  field: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  suggestion?: string;
}

export interface ValidationWarning {
  id: string;
  field: string;
  message: string;
  suggestion?: string;
}

export interface ValidationReport {
  id: string;
  timestamp: Date;
  dataType: string;
  overallScore: number;
  results: ValidationResult;
  recommendations: string[];
  complianceStatus: 'compliant' | 'non_compliant' | 'needs_review';
}

// Менеджер валидации данных
export class DataValidationManager {
  private static instance: DataValidationManager;
  private validationRules: ValidationRule[] = [];
  private validationHistory: ValidationReport[] = [];

  private constructor() {
    this.initializeValidationRules();
  }

  public static getInstance(): DataValidationManager {
    if (!DataValidationManager.instance) {
      DataValidationManager.instance = new DataValidationManager();
    }
    return DataValidationManager.instance;
  }

  // Инициализация правил валидации
  private initializeValidationRules(): void {
    this.validationRules = [
      // Правила безопасности
      {
        id: 'age-range',
        name: 'Диапазон возраста',
        description: 'Проверка корректности возрастного диапазона',
        category: 'safety',
        severity: 'critical',
        validator: (data: any) => {
          const errors: ValidationError[] = [];
          const warnings: ValidationWarning[] = [];
          
          if (data.age !== undefined) {
            if (data.age < 0) {
              errors.push({
                id: 'age-negative',
                field: 'age',
                message: 'Возраст не может быть отрицательным',
                severity: 'critical',
                suggestion: 'Проверьте правильность ввода возраста'
              });
            } else if (data.age > 18) {
              warnings.push({
                id: 'age-adult',
                field: 'age',
                message: 'Возраст превышает 18 лет - проверьте корректность данных',
                suggestion: 'Убедитесь, что это педиатрическое приложение'
              });
            } else if (data.age < 0.1) {
              warnings.push({
                id: 'age-newborn',
                field: 'age',
                message: 'Возраст менее 1 месяца - требуются особые расчеты',
                suggestion: 'Используйте специальные формулы для новорожденных'
              });
            }
          }
          
          return {
            isValid: errors.length === 0,
            errors,
            warnings,
            score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 0,
            timestamp: new Date()
          };
        },
        errorMessage: 'Некорректный возраст',
        warningMessage: 'Возраст требует внимания'
      },

      // Правила точности
      {
        id: 'dosage-calculation',
        name: 'Расчет дозировки',
        description: 'Проверка корректности расчета дозировки',
        category: 'accuracy',
        severity: 'critical',
        validator: (data: any) => {
          const errors: ValidationError[] = [];
          const warnings: ValidationWarning[] = [];
          
          if (data.dosage && data.weight && data.medication) {
            const maxDose = this.getMaxDoseForMedication(data.medication, data.age, data.weight);
            if (maxDose > 0) {
              if (data.dosage > maxDose) {
                errors.push({
                  id: 'dosage-exceeded',
                  field: 'dosage',
                  message: `Дозировка ${data.dosage} мг превышает максимальную дозу ${maxDose} мг`,
                  severity: 'critical',
                  suggestion: 'Снизьте дозировку до безопасного уровня'
                });
              } else if (data.dosage > maxDose * 0.8) {
                warnings.push({
                  id: 'dosage-high',
                  field: 'dosage',
                  message: `Дозировка близка к максимальной (${Math.round((data.dosage/maxDose)*100)}% от максимума)`,
                  suggestion: 'Проверьте необходимость такой высокой дозы'
                });
              }
            }
          }
          
          return {
            isValid: errors.length === 0,
            errors,
            warnings,
            score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 0,
            timestamp: new Date()
          };
        },
        errorMessage: 'Некорректная дозировка',
        warningMessage: 'Дозировка требует проверки'
      },

      // Правила полноты
      {
        id: 'required-fields',
        name: 'Обязательные поля',
        description: 'Проверка заполнения обязательных полей',
        category: 'completeness',
        severity: 'high',
        validator: (data: any) => {
          const errors: ValidationError[] = [];
          const warnings: ValidationWarning[] = [];
          
          const requiredFields = ['age', 'weight', 'medication'];
          requiredFields.forEach(field => {
            if (!data[field] || data[field] === '') {
              errors.push({
                id: `missing-${field}`,
                field,
                message: `Обязательное поле "${field}" не заполнено`,
                severity: 'high',
                suggestion: 'Заполните все обязательные поля'
              });
            }
          });
          
          return {
            isValid: errors.length === 0,
            errors,
            warnings,
            score: errors.length === 0 ? 100 : 0,
            timestamp: new Date()
          };
        },
        errorMessage: 'Не заполнены обязательные поля',
        warningMessage: 'Проверьте полноту данных'
      },

      // Правила согласованности
      {
        id: 'age-weight-consistency',
        name: 'Согласованность возраста и веса',
        description: 'Проверка соответствия возраста и веса',
        category: 'consistency',
        severity: 'medium',
        validator: (data: any) => {
          const errors: ValidationError[] = [];
          const warnings: ValidationWarning[] = [];
          
          if (data.age && data.weight) {
            const expectedWeightRanges = this.getExpectedWeightRanges(data.age);
            if (expectedWeightRanges) {
              if (data.weight < expectedWeightRanges.min) {
                warnings.push({
                  id: 'weight-low',
                  field: 'weight',
                  message: `Вес ${data.weight} кг ниже ожидаемого для возраста ${data.age} лет`,
                  suggestion: 'Проверьте правильность ввода веса'
                });
              } else if (data.weight > expectedWeightRanges.max) {
                warnings.push({
                  id: 'weight-high',
                  field: 'weight',
                  message: `Вес ${data.weight} кг выше ожидаемого для возраста ${data.age} лет`,
                  suggestion: 'Проверьте правильность ввода веса'
                });
              }
            }
          }
          
          return {
            isValid: errors.length === 0,
            errors,
            warnings,
            score: errors.length === 0 ? (warnings.length === 0 ? 100 : 80) : 0,
            timestamp: new Date()
          };
        },
        errorMessage: 'Несоответствие возраста и веса',
        warningMessage: 'Проверьте согласованность данных'
      }
    ];
  }

  // Валидация данных
  public validateData(data: any, dataType: string = 'medical'): ValidationReport {
    const allErrors: ValidationError[] = [];
    const allWarnings: ValidationWarning[] = [];
    let totalScore = 0;
    let validRules = 0;

    // Применяем все правила валидации
    this.validationRules.forEach(rule => {
      try {
        const result = rule.validator(data);
        allErrors.push(...result.errors);
        allWarnings.push(...result.warnings);
        totalScore += result.score;
        if (result.isValid) validRules++;
      } catch (error) {
        allErrors.push({
          id: `rule-error-${rule.id}`,
          field: 'system',
          message: `Ошибка в правиле валидации: ${rule.name}`,
          severity: 'high',
          suggestion: 'Обратитесь к администратору'
        });
      }
    });

    const averageScore = this.validationRules.length > 0 ? totalScore / this.validationRules.length : 0;
    const complianceStatus = this.determineComplianceStatus(averageScore, allErrors.length);

    const report: ValidationReport = {
      id: this.generateId(),
      timestamp: new Date(),
      dataType,
      overallScore: Math.round(averageScore),
      results: {
        isValid: allErrors.length === 0,
        errors: allErrors,
        warnings: allWarnings,
        score: Math.round(averageScore),
        timestamp: new Date()
      },
      recommendations: this.generateRecommendations(allErrors, allWarnings),
      complianceStatus
    };

    // Сохраняем в историю
    this.validationHistory.unshift(report);
    if (this.validationHistory.length > 1000) {
      this.validationHistory = this.validationHistory.slice(0, 1000);
    }

    return report;
  }

  // Получение максимальной дозы для препарата
  private getMaxDoseForMedication(medication: string, age?: number, weight?: number): number {
    const maxDoses: { [key: string]: number } = {
      'paracetamol': 60, // мг/кг/день
      'ibuprofen': 40,   // мг/кг/день
      'amoxicillin': 90, // мг/кг/день
      'azithromycin': 10, // мг/кг/день
      'cetirizine': 10,  // мг/день
      'levocetirizine': 5, // мг/день
    };
    
    const baseMaxDose = maxDoses[medication.toLowerCase()] || 0;
    
    // Корректировка по возрасту
    if (age && age < 2) {
      return baseMaxDose * 0.5; // Снижение дозы для детей до 2 лет
    }
    
    return baseMaxDose;
  }

  // Получение ожидаемых диапазонов веса по возрасту
  private getExpectedWeightRanges(age: number): { min: number; max: number } | null {
    const weightRanges: { [key: number]: { min: number; max: number } } = {
      0: { min: 2.5, max: 4.5 },    // новорожденные
      1: { min: 6, max: 12 },       // 1 год
      2: { min: 9, max: 16 },       // 2 года
      3: { min: 12, max: 20 },      // 3 года
      5: { min: 16, max: 28 },      // 5 лет
      10: { min: 25, max: 50 },     // 10 лет
      15: { min: 40, max: 80 },     // 15 лет
    };

    // Находим ближайший возраст
    const ages = Object.keys(weightRanges).map(Number).sort((a, b) => a - b);
    let closestAge = ages[0];
    
    for (const ageKey of ages) {
      if (age >= ageKey) {
        closestAge = ageKey;
      } else {
        break;
      }
    }

    return weightRanges[closestAge] || null;
  }

  // Определение статуса соответствия
  private determineComplianceStatus(score: number, errorCount: number): 'compliant' | 'non_compliant' | 'needs_review' {
    if (errorCount > 0) {
      return 'non_compliant';
    } else if (score < 80) {
      return 'needs_review';
    } else {
      return 'compliant';
    }
  }

  // Генерация рекомендаций
  private generateRecommendations(errors: ValidationError[], warnings: ValidationWarning[]): string[] {
    const recommendations: string[] = [];
    
    if (errors.length > 0) {
      recommendations.push('Исправьте критические ошибки перед продолжением');
    }
    
    if (warnings.length > 0) {
      recommendations.push('Проверьте предупреждения для улучшения качества данных');
    }
    
    const errorFields = errors.map(e => e.field);
    const errorCategories = Array.from(new Set(errorFields));
    errorCategories.forEach(field => {
      recommendations.push(`Особое внимание к полю: ${field}`);
    });
    
    return recommendations;
  }

  // Получение истории валидации
  public getValidationHistory(limit?: number): ValidationReport[] {
    return limit ? this.validationHistory.slice(0, limit) : this.validationHistory;
  }

  // Получение статистики валидации
  public getValidationStats(): {
    totalValidations: number;
    averageScore: number;
    complianceRate: number;
    errorRate: number;
    warningRate: number;
  } {
    const totalValidations = this.validationHistory.length;
    if (totalValidations === 0) {
      return {
        totalValidations: 0,
        averageScore: 0,
        complianceRate: 0,
        errorRate: 0,
        warningRate: 0
      };
    }

    const totalScore = this.validationHistory.reduce((sum, report) => sum + report.overallScore, 0);
    const compliantCount = this.validationHistory.filter(report => report.complianceStatus === 'compliant').length;
    const errorCount = this.validationHistory.reduce((sum, report) => sum + report.results.errors.length, 0);
    const warningCount = this.validationHistory.reduce((sum, report) => sum + report.results.warnings.length, 0);

    return {
      totalValidations,
      averageScore: Math.round(totalScore / totalValidations),
      complianceRate: Math.round((compliantCount / totalValidations) * 100),
      errorRate: Math.round((errorCount / totalValidations) * 100),
      warningRate: Math.round((warningCount / totalValidations) * 100)
    };
  }

  // Экспорт отчета валидации
  public exportValidationReport(reportId: string): string | null {
    const report = this.validationHistory.find(r => r.id === reportId);
    if (!report) return null;

    return `
ОТЧЕТ О ВАЛИДАЦИИ МЕДИЦИНСКИХ ДАННЫХ
ID отчета: ${report.id}
Дата: ${report.timestamp.toLocaleString('ru-RU')}
Тип данных: ${report.dataType}

ОБЩАЯ ОЦЕНКА: ${report.overallScore}/100
СТАТУС СООТВЕТСТВИЯ: ${report.complianceStatus.toUpperCase()}

ОШИБКИ (${report.results.errors.length}):
${report.results.errors.map(error => 
  `- ${error.field}: ${error.message} (${error.severity})`
).join('\n')}

ПРЕДУПРЕЖДЕНИЯ (${report.results.warnings.length}):
${report.results.warnings.map(warning => 
  `- ${warning.field}: ${warning.message}`
).join('\n')}

РЕКОМЕНДАЦИИ:
${report.recommendations.map(rec => `- ${rec}`).join('\n')}
    `.trim();
  }

  // Генерация ID
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

// Экспорт глобального экземпляра
export const dataValidationManager = DataValidationManager.getInstance();
